import React, { useState } from 'react';
import { 
  X, CheckCircle, MapPin, 
  Coins, Info, Loader2 
} from 'lucide-react';
import { db } from '../../services/firebase'; 
import { 
  doc, 
  updateDoc, 
  increment, 
  addDoc, 
  collection, 
  serverTimestamp
} from 'firebase/firestore';
import { UserProfile } from '../../types'; 

interface LoveScoopStoreProps {
  onClose: () => void;
  userProfile: UserProfile | null;
  onUpdateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  onOpenBuyCredits: () => void; 
}

// --- HARDCODED PLATFORM ITEMS ---
const CREDIT_ITEMS = [
  {
    id: 'ls-free-shipping',
    name: 'Free Shipping Voucher',
    description: 'Waive shipping fees on your next hype drop order.',
    price: 50000,
    image: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/shippingscoopp.jpg?alt=media&token=d37f686b-75de-4396-9f49-a3f8a2726ce2' 
  },
  {
    id: 'ls-r500-credit',
    name: '50% OFF',
    description: 'Official platform gift card for any streetwear purchase.',
    price: 50000,
    image: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/50%25%20off%20prize.png?alt=media&token=e48fe8e1-e738-42d0-a280-8f76a6e5e9ad'
  },
  {
    id: 'ls-mystery-box',
    name: 'Hype Mystery Box',
    description: 'Unlock a random prize. Ranging from 5% discounts to free sneakers.',
    price: 5000000,
    image: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/Scoop%20Mystery%20Box%20Prize.png?alt=media&token=59b1b972-585f-409a-b781-f222a21e29c3'
  },
  {
    id: 'ls-golden-ticket',
    name: 'Golden Ticket',
    description: 'Apply a 3x Luck Multiplier to your next giveaway entry.',
    price: 25000,
    image: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/shippingscoopp.jpg?alt=media&token=d37f686b-75de-4396-9f49-a3f8a2726ce2'
  }
];

const CreditItemCard = ({ item, onClick }: any) => (
  <div onClick={() => onClick(item)} className="group cursor-pointer">
    <div className="aspect-[1.5/1] bg-black border border-zinc-800 relative overflow-hidden mb-3 rounded-2xl shadow-xl transition-all hover:-translate-y-1 duration-300">
      <img src={item.image} className="w-full h-full object-cover transition-opacity" alt={item.name} />
      <div className="absolute inset-0 p-4 flex flex-col justify-end bg-gradient-to-t from-black/80 to-transparent">
        <h4 className="font-black italic text-lg uppercase text-white leading-none">{item.name}</h4>
      </div>
    </div>
    <div className="flex justify-between items-start">
      <p className="text-xs text-zinc-500 font-bold uppercase w-2/3 line-clamp-2 leading-tight group-hover:text-white transition-colors">{item.description}</p>
      <span className="flex items-center gap-1 bg-white text-black px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest shrink-0 shadow-lg">
        <Coins size={10}/> {item.price}
      </span>
    </div>
  </div>
);

export const LoveScoopStore: React.FC<LoveScoopStoreProps> = ({ 
  onClose, userProfile, onUpdateProfile, onOpenBuyCredits 
}) => {
  const [activeTab, setActiveTab] = useState<'shop' | 'about'>('shop');
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // 🟢 NEW STATE FOR CUSTOM SUCCESS MODAL
  const [successItem, setSuccessItem] = useState<any | null>(null);

  const STORE_ASSETS = {
    logo: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/cube%20(2).jpg?alt=media&token=7d2957e1-e840-422b-9f4e-c9669bf37cf1',
    banner: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/love%20scoopp.jpg?alt=media&token=70ac832d-fb42-47e9-934a-b97023f844e5' 
  };

  const bColor = '#ffffff';
  const userBalance = userProfile?.cred || 0;

  const handlePurchase = async () => {
    if (!userProfile || !selectedItem) return;
    
    if (userBalance < selectedItem.price) return;

    setIsProcessing(true);
    try {
      const userRef = doc(db, 'users', userProfile.uid);
      await updateDoc(userRef, { 
        cred: increment(-selectedItem.price) 
      });
      
      // 🟢 UPDATED: Sending the full payload with image, description, and serverTimestamp!
      await addDoc(collection(db, 'rewards'), {
          userId: userProfile.uid,
          rewardType: selectedItem.id === 'ls-mystery-box' || selectedItem.id === 'ls-golden-ticket' ? 'item' : 'voucher',
          type: selectedItem.id === 'ls-mystery-box' || selectedItem.id === 'ls-golden-ticket' ? 'item' : 'voucher', // fallback
          title: selectedItem.name,
          name: selectedItem.name, // fallback
          description: selectedItem.description,
          image: selectedItem.image, // 🟢 Vault needs this to render the card!
          sourceGame: 'Love.Scoop Store',
          source: 'Love.Scoop Store',
          status: 'unclaimed',
          createdAt: serverTimestamp() // 🟢 Vaults usually break if this isn't a Firebase Timestamp
      });

      await onUpdateProfile({ cred: userBalance - selectedItem.price });
      
      // Show custom success modal
      setSuccessItem(selectedItem);
      setSelectedItem(null);
    } catch (error) {
      console.error("Purchase error:", error);
      alert("Purchase failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col animate-in fade-in duration-300 bg-black text-white font-sans">
      
      <style>{`
        @keyframes white-pulse {
          0% { box-shadow: 0 0 5px rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); }
          50% { box-shadow: 0 0 20px rgba(255,255,255,0.4); border-color: rgba(255,255,255,1); }
          100% { box-shadow: 0 0 5px rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); }
        }
        .premium-glow-btn {
          animation: white-pulse 3s infinite ease-in-out;
        }
      `}</style>

      {/* 📱 MOBILE LAYOUT */}
      <div className="lg:hidden flex-1 overflow-y-auto relative scrollbar-hide">
          <div className="relative h-64 w-full shrink-0 bg-zinc-900 border-b border-zinc-800">
            <img src={STORE_ASSETS.banner} className="w-full h-full object-cover opacity-80" alt="Banner" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
            <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center z-20">
                <button onClick={onClose} className="w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10"><X size={20} /></button>
            </div>
          </div>

          <div className="px-6 -mt-16 relative z-10 flex flex-col pb-6 items-center text-center">
             <div className="w-24 h-24 rounded-[2rem] border-4 border-black bg-zinc-950 overflow-hidden shadow-2xl mb-4 flex items-center justify-center">
                <img src={STORE_ASSETS.logo} className="w-full h-full object-cover" alt="Logo" />
             </div>
             <div className="flex items-center gap-2 mb-2">
                <h1 className="text-3xl font-black uppercase tracking-tight text-white">Love.Scoop</h1>
                <CheckCircle size={18} className="text-white" fill="currentColor" />
             </div>
             <p className="text-sm text-zinc-400 font-medium leading-snug mb-4">Official platform rewards & digital exclusives.</p>
             <span className="px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-full text-[10px] font-black uppercase tracking-widest text-zinc-500 shadow-md">Official Store</span>
          </div>

          <div className="p-4 pb-24">
            <div className="flex border-b border-zinc-800 sticky top-0 bg-black z-20 backdrop-blur-md bg-opacity-80 mb-6">
                {['shop', 'about'].map(t => (
                    <button 
                        key={t} onClick={() => setActiveTab(t as any)} 
                        className="flex-1 py-4 text-[10px] font-black uppercase tracking-[0.2em] transition-colors"
                        style={{ color: activeTab === t ? bColor : '#71717a', borderBottom: activeTab === t ? `2px solid ${bColor}` : 'none' }}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {activeTab === 'shop' && (
              <div className="grid grid-cols-1 gap-4">
                {CREDIT_ITEMS.map((item) => (
                  <CreditItemCard key={item.id} item={item} onClick={setSelectedItem} />
                ))}
              </div>
            )}
            
            {activeTab === 'about' && (
              <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
                  <h3 className="text-xs font-black uppercase tracking-widest text-white mb-3 flex items-center gap-2"><Info size={14}/> About</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">Spend your earned platform credits here on exclusive drops, free shipping vouchers, and hype multipliers. Credits only. No cash accepted.</p>
              </div>
            )}
          </div>
      </div>

      {/* 🖥️ DESKTOP LAYOUT */}
      <div className="hidden lg:flex w-full h-full bg-black">
          <div className="w-[400px] xl:w-[500px] border-r border-zinc-800 flex flex-col h-full overflow-y-auto shrink-0 bg-black relative">
              <div className="h-64 w-full relative shrink-0 bg-zinc-900">
                  <img src={STORE_ASSETS.banner} className="w-full h-full object-cover opacity-60" alt="Banner" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                  <div className="absolute top-6 left-6"><button onClick={onClose} className="p-2 bg-black text-white hover:bg-white hover:text-black transition-colors border border-zinc-800"><X size={20}/></button></div>
              </div>

              <div className="p-12 -mt-20 relative z-10 flex flex-col flex-1 items-start text-left">
                  <div className="w-24 h-24 border border-zinc-800 bg-zinc-950 flex items-center justify-center mb-8 rounded-2xl shadow-2xl overflow-hidden">
                     <img src={STORE_ASSETS.logo} className="w-full h-full object-cover" alt="Logo" />
                  </div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <h1 className="text-5xl xl:text-6xl font-black italic uppercase tracking-tighter leading-[0.8] text-white">Love.Scoop</h1>
                    <CheckCircle size={24} className="text-white" fill="currentColor" />
                  </div>
                  <p className="text-sm text-zinc-500 font-bold uppercase tracking-widest mb-8 line-clamp-3">Official platform rewards & digital exclusives.</p>

                  <div className="mt-auto w-full p-4 border border-zinc-800 bg-zinc-900 rounded-xl text-center shadow-lg">
                     <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">Official Platform Store</span>
                  </div>
              </div>
          </div>

          <div className="flex-1 flex flex-col h-full bg-black relative">
              <div className="flex border-b border-zinc-800 bg-black sticky top-0 z-10">
                  {['shop', 'about'].map(t => (
                      <button 
                          key={t} onClick={() => setActiveTab(t as any)} 
                          className="px-12 py-6 text-xs font-bold uppercase tracking-[0.2em] border-r border-zinc-800 transition-colors"
                          style={{ color: activeTab === t ? bColor : '#71717a', borderBottom: activeTab === t ? `2px solid ${bColor}` : 'none' }}
                      >
                          {t}
                      </button>
                  ))}
              </div>

              <div className="flex-1 overflow-y-auto p-12">
                  {activeTab === 'shop' && (
                      <section>
                          <div className="flex items-center gap-4 mb-8 border-b border-zinc-800 pb-4">
                              <Coins size={20} className="text-white" />
                              <h3 className="text-2xl font-black italic uppercase text-white">Credit Exclusives</h3>
                          </div>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-12">
                            {CREDIT_ITEMS.map((item) => (
                              <CreditItemCard key={item.id} item={item} onClick={setSelectedItem} />
                            ))}
                          </div>
                      </section>
                  )}

                  {activeTab === 'about' && (
                      <div className="max-w-2xl text-zinc-400 leading-relaxed font-mono text-sm">
                          <p className="mb-8">Spend your earned platform credits here on exclusive drops, free shipping vouchers, and hype multipliers. Credits only. No cash accepted.</p>
                      </div>
                  )}
              </div>
          </div>
      </div>

      {/* 🟢 CHECKOUT CONFIRMATION MODAL */}
      {selectedItem && !successItem && (
          <div className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md flex items-center justify-center p-6" onClick={() => !isProcessing && setSelectedItem(null)}>
              <div className="bg-zinc-950 border border-zinc-800 rounded-[2rem] lg:rounded-none w-full max-w-md p-8 shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-20 h-16 bg-black rounded-lg border border-zinc-800 overflow-hidden">
                        <img src={selectedItem.image} className="w-full h-full object-cover" />
                    </div>
                    <button onClick={() => !isProcessing && setSelectedItem(null)} className="p-2 text-zinc-500 hover:text-white transition-colors bg-black rounded-full lg:rounded-none border border-zinc-800"><X size={20}/></button>
                  </div>
                  
                  <h3 className="text-2xl font-black italic uppercase text-white mb-2">{selectedItem.name}</h3>
                  <p className="text-sm text-zinc-400 font-medium mb-8">{selectedItem.description}</p>

                  <div className="bg-black border border-zinc-800 rounded-2xl lg:rounded-none p-6 mb-8 space-y-4">
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-zinc-500 font-bold uppercase tracking-widest">Cost</span>
                      <span className="text-white font-black flex items-center gap-1"><Coins size={14}/> {selectedItem.price}</span>
                    </div>
                    <div className="h-[1px] w-full bg-zinc-800"></div>
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-zinc-500 font-bold uppercase tracking-widest">Your Balance</span>
                      <span className={`${userBalance < selectedItem.price ? 'text-red-500' : 'text-green-400'} font-black flex items-center gap-1`}>
                        <Coins size={14}/> {userBalance}
                      </span>
                    </div>
                  </div>

                  {userBalance >= selectedItem.price ? (
                      <button 
                        onClick={handlePurchase} 
                        disabled={isProcessing}
                        className="w-full py-5 bg-white text-black rounded-full lg:rounded-none font-black uppercase tracking-[0.2em] text-xs hover:bg-zinc-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2 premium-glow-btn shadow-xl"
                      >
                        {isProcessing ? <Loader2 className="animate-spin" size={16} /> : <Coins size={16} />}
                        Redeem Now
                      </button>
                  ) : (
                      <button 
                        onClick={() => { setSelectedItem(null); onOpenBuyCredits(); }} 
                        className="w-full py-5 bg-black border border-zinc-800 text-white rounded-full lg:rounded-none font-black uppercase tracking-[0.2em] text-xs transition-all flex items-center justify-center gap-2 hover:border-white hover:bg-white hover:text-black shadow-xl"
                      >
                        <Coins size={16} /> Get More Credits
                      </button>
                  )}
              </div>
          </div>
      )}

      {/* 🟢 CUSTOM SUCCESS MODAL UI */}
      {successItem && (
          <div className="fixed inset-0 z-[130] bg-black/90 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in" onClick={() => setSuccessItem(null)}>
              <div className="bg-zinc-950 border border-zinc-800 rounded-[2rem] lg:rounded-none w-full max-w-sm p-10 flex flex-col items-center text-center shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
                  <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center text-black mb-8 shadow-[0_0_40px_rgba(255,255,255,0.3)]">
                      <CheckCircle size={40} strokeWidth={2.5} />
                  </div>
                  <h3 className="text-3xl font-black italic uppercase text-white tracking-tighter mb-2 leading-none">Acquired</h3>
                  <p className="text-sm text-zinc-400 font-mono uppercase tracking-widest mb-8 border-b border-zinc-800 pb-8 w-full">
                      {successItem.name} sent to Vault.
                  </p>
                  <button 
                      onClick={() => setSuccessItem(null)}
                      className="w-full py-4 border border-zinc-800 bg-black text-white hover:bg-white hover:text-black transition-colors rounded-full lg:rounded-none font-black uppercase tracking-widest text-[10px]"
                  >
                      Acknowledge
                  </button>
              </div>
          </div>
      )}
    </div>
  );
};